import { useState } from 'react'
import { api, istSchritt, type Ich, type Schritt } from '../api'
import { QrKode, Schluessel } from '../komponenten/QrKode'

/* Die Anmeldeseite.
 *
 * Sie sagt bei einer abgelehnten Anmeldung genau das, was die API sagt,
 * und nichts darüber hinaus: „E-Mail oder Passwort stimmt nicht." Wer
 * erfährt, dass es die Adresse gibt, hat schon die Hälfte — und bei
 * einem Standort mit zwölf Häusern sind die Adressen nicht schwer zu
 * raten.
 *
 * Kein „Passwort vergessen": Es gibt niemanden, der eine Zurücksetzung
 * verschicken könnte. Wer sein Passwort verliert, bekommt vom
 * Betreiber ein neues — das ist bei zwei bis fünf Kunden der ehrlichere
 * Weg als ein Postfach, das niemand betreut.
 *
 * Seit dem zweiten Faktor sind es zwei Schritte. Das Merkmal dazwischen
 * steht **im Cookie** und nicht in dieser Datei: Es ist HttpOnly, kein
 * Skript kommt daran, und der Browser schickt es beim zweiten Schritt
 * von allein mit.
 */

/* Die Ersatzcodes. Einmal und nie wieder.
 *
 * Deshalb steht hier ein Hinweis, der sonst nirgends steht, und
 * deshalb geht es erst weiter, wenn jemand bestätigt hat, sie zu haben.
 * Ein Fenster, das man wegklickt, ist bei Codes, die nur einmal
 * kommen, kein Fenster, sondern ein Verlust.
 */
function Ersatzcodes({ codes, weiter }:
                     { codes: string[]; weiter: () => void }) {
  const [gesichert, setGesichert] = useState(false)
  const text = codes.join('\n')

  return (
    <div className="anmelde-karte">
      <div className="anmelde-marke">
        <b>Ersatzcodes</b>
        <span>Für den Fall, dass das Telefon weg ist</span>
      </div>

      <p className="dezent">
        Jeder gilt <b>genau einmal</b> und ersetzt die Kennzahl aus der
        App. Sie werden <b>jetzt und nie wieder</b> angezeigt — danach
        steht nur noch ihre Prüfsumme in der Datenbank.
      </p>

      <ol className="ersatzcodes">
        {codes.map((c) => <li key={c}><code>{c}</code></li>)}
      </ol>

      <div className="knopfreihe">
        <button type="button" onClick={() => {
          void navigator.clipboard?.writeText(text).catch(() => undefined)
        }}>Kopieren</button>
        <button type="button" onClick={() => {
          /* Ein Ausdruck ist bei so etwas kein Rückschritt: Ein Zettel
           * im Safe ist genau dann da, wenn das Telefon es nicht ist. */
          window.print()
        }}>Drucken</button>
      </div>

      <label className="kaestchen">
        <input type="checkbox" checked={gesichert}
               onChange={(e) => setGesichert(e.target.checked)} />
        Ich habe die Codes gesichert.
      </label>

      <button className="anmelde-knopf" type="button"
              disabled={!gesichert} onClick={weiter}>
        Weiter
      </button>
    </div>
  )
}

/* Ausgeführt, weil es zwei Wege hierher gibt: die Anmeldung und das
 * Einlösen einer Einladung. Zweimal geschrieben liefen sie
 * auseinander, und der seltenere Weg wäre der mit dem Fehler. */
export function ZweiterSchritt({ schritt, fertig, zurueck }:
                        { schritt: Schritt; fertig: (ich: Ich) => void
                          zurueck: () => void }) {
  const [kennzahl, setKennzahl] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [ich, setIch] = useState<Ich | null>(null)

  const einrichten = schritt.zweiter_faktor === 'EINRICHTEN'

  const abschicken = async (e: React.FormEvent) => {
    e.preventDefault()
    setFehler(null)
    setLaeuft(true)
    try {
      const a = await api.kennzahl(kennzahl)
      if (a.ersatzcodes?.length) { setCodes(a.ersatzcodes); setIch(a) }
      else fertig(a)
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err))
      setKennzahl('')
    } finally {
      setLaeuft(false)
    }
  }

  if (codes && ich) {
    return (
      <div className="anmelde-seite">
        <Ersatzcodes codes={codes} weiter={() => fertig(ich)} />
      </div>
    )
  }

  return (
    <div className="anmelde-seite">
      <form className="anmelde-karte" onSubmit={abschicken}>
        <div className="anmelde-marke">
          <b>{einrichten ? 'Zweiter Faktor einrichten' : 'Kennzahl'}</b>
          <span>
            {einrichten
              ? 'Ihre Stufe sieht alle Mandanten — dafür ist ein Passwort allein zu wenig.'
              : 'Die sechsstellige Zahl aus Ihrer Authenticator-App'}
          </span>
        </div>

        {einrichten && schritt.einrichtung && (
          <div className="einrichtung">
            <p className="dezent">
              Mit einer Authenticator-App abfotografieren — Google
              Authenticator, Aegis, 1Password, was Sie haben.
            </p>
            <QrKode inhalt={schritt.einrichtung} />
            <Schluessel adresse={schritt.einrichtung} />
          </div>
        )}

        <label htmlFor="kz">
          {einrichten ? 'Kennzahl aus der App' : 'Kennzahl'}
        </label>
        <input id="kz" inputMode="numeric" autoComplete="one-time-code"
               autoFocus required value={kennzahl}
               placeholder="000000"
               onChange={(e) => setKennzahl(e.target.value)} />

        {!einrichten && (
          <p className="anmelde-fuss">
            Telefon verloren? Statt der Kennzahl geht auch ein
            Ersatzcode — dasselbe Feld.
          </p>
        )}

        {fehler && <div className="anmelde-fehler" role="alert">{fehler}</div>}

        <button className="anmelde-knopf" type="submit" disabled={laeuft}>
          {laeuft ? 'Einen Moment …' : 'Weiter'}
        </button>

        <button className="verweis" type="button" onClick={zurueck}>
          Zurück zur Anmeldung
        </button>
      </form>
    </div>
  )
}

export function Anmeldung({ fertig }: { fertig: (ich: Ich) => void }) {
  const [kennung, setKennung] = useState('')
  const [email, setEmail] = useState('')
  const [passwort, setPasswort] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [schritt, setSchritt] = useState<Schritt | null>(null)

  const abschicken = async (e: React.FormEvent) => {
    e.preventDefault()
    setFehler(null)
    setLaeuft(true)
    try {
      const a = await api.anmelden(email, passwort, kennung.trim())
      if (istSchritt(a)) setSchritt(a)
      else fertig(a)
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err))
      setPasswort('')
    } finally {
      setLaeuft(false)
    }
  }

  if (schritt) {
    return <ZweiterSchritt schritt={schritt} fertig={fertig}
                           zurueck={() => { setSchritt(null); setPasswort('') }} />
  }

  return (
    <div className="anmelde-seite">
      <form className="anmelde-karte" onSubmit={abschicken}>
        <div className="anmelde-marke">
          <b>Gastro Audit</b>
          <span>Auswertung und Prüfung von Kassendaten</span>
        </div>

        <label htmlFor="kennung">Kundenkennung</label>
        <input id="kennung" type="text" autoComplete="organization"
               autoCapitalize="none" spellCheck={false} autoFocus
               value={kennung} onChange={(e) => setKennung(e.target.value)} />
        <p className="dezent">Als Betreiber leer lassen.</p>

        <label htmlFor="email">E-Mail</label>
        <input id="email" type="email" autoComplete="username" required
               value={email}
               onChange={(e) => setEmail(e.target.value)} />

        <label htmlFor="passwort">Passwort</label>
        <input id="passwort" type="password" autoComplete="current-password"
               required value={passwort}
               onChange={(e) => setPasswort(e.target.value)} />

        {fehler && <div className="anmelde-fehler" role="alert">{fehler}</div>}

        <button className="anmelde-knopf" type="submit" disabled={laeuft}>
          {laeuft ? 'Einen Moment …' : 'Anmelden'}
        </button>

        <p className="anmelde-fuss">
          Kein Zugang? Der Betreiber legt die Konten an — eine
          Selbstregistrierung gibt es nicht.
        </p>
      </form>
    </div>
  )
}
